import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from "@azure/msal-browser";

const tenantId = import.meta.env.VITE_AAD_TENANT_ID ?? "common";
const clientId = import.meta.env.VITE_AAD_CLIENT_ID ?? "00000000-0000-0000-0000-000000000000";
const audience = import.meta.env.VITE_AAD_AUDIENCE ?? clientId;

const config: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin + "/auth/callback",
    // Default true would navigate the browser BACK to wherever loginRedirect()
    // was called from (e.g. /signin), bypassing the Vue Router redirect rule
    // for /auth/callback → /. With it off, the browser lands on
    // /auth/callback after Microsoft redirects back and the Router takes
    // over from there.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "sessionStorage",
  },
};

export const apiScope = `api://${audience}/Playgrounds.Access`;

export const msalInstance = new PublicClientApplication(config);

let initialized = false;

export async function initMsal(): Promise<void> {
  if (initialized) return;
  await msalInstance.initialize();
  // Handle the redirect callback before anything else touches account state.
  await msalInstance.handleRedirectPromise();
  initialized = true;
}

export function getAccount(): AccountInfo | null {
  const accounts = msalInstance.getAllAccounts();
  return accounts[0] ?? null;
}

export async function loginRedirect(): Promise<void> {
  await msalInstance.loginRedirect({ scopes: [apiScope] });
}

export async function logoutRedirect(): Promise<void> {
  await msalInstance.logoutRedirect();
}

export async function acquireToken(): Promise<string> {
  const account = getAccount();
  if (!account) throw new InteractionRequiredAuthError("no_account");
  try {
    const r = await msalInstance.acquireTokenSilent({ scopes: [apiScope], account });
    return r.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: [apiScope], account });
      // Redirect navigates away; throw to short-circuit callers.
      throw e;
    }
    throw e;
  }
}
